/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { Route, Switch } from "react-router-dom";

import { initializeIcons } from "office-ui-fabric-react/lib/Icons";

import { Stack } from "@fluentui/react";

import Login from "Authentication/Login";
import PrivateRoute from "Authentication/PrivateRoute";
import DistributionListDetails from "Components/DistributionListDetails/DistributionListDetails";
import StudentDetails from "Components/StudentDetails/StudentDetails";
import { TenantTypesEnum } from "Constants/TenantTypes";
import AccessControlContainer from "Containers/AccessControl/AccessControlContainer";
import AccessControlCreateEditContainer from "Containers/AccessControl/AccessControlCreateEditContainer/AccessControlCreateEditContainer";
import ApplicationsContainer from "Containers/Applications/ApplicationsContainer";
import { IApplicationsContainerProps } from "Containers/Applications/IApplicationsContainerProps";
import AwardCategoriesContainer from "Containers/AwardCategories/AwardCategoriesContainer";
import AzureBenefitRequestDetailsContainer from "Containers/Benefits/AzureBenefitRequestDetailsContainer/AzureBenefitRequestDetailsContainer";
import AzureRequestsContainer from "Containers/Benefits/AzureBenefitRequestsContainer/AzureBenefitRequestsContainer";
import BenefitsContainer from "Containers/Benefits/BenefitsContainer";
import M365CopilotBenefitContainer from "Containers/Benefits/M365CopilotBenefitContainer/M365CopilotBenefitContainer";
import ManageCodesContainer from "Containers/Benefits/ManageCodesContainer/ManageCodesContainer";
import VisualStudioBenefitContainer from "Containers/Benefits/VisualStudioBenefitContainer/VisualStudioBenefitContainer";
import DemographicsContainer from "Containers/Demographics/DemographicsContainer";
import DistributionListRequestsContainer from "Containers/DistributionLists/DistributionListRequestsContainer";
import DistributionListsContainer from "Containers/DistributionLists/DistributionListsContainer";
import EventsContainer from "Containers/Events/EventsContainer";
import HomeContainer from "Containers/Home/HomeContainer";
import MvpEventContainer from "Containers/MVP/Events/MvpEventContainer";
import MvpHomeContainer from "Containers/MVP/Home/MvpHomeContainer";
import MostValuableProfessionalApplicationDetailsContainer from "Containers/MVP/MostValuableProfessionalApplicationDetailsContainer/MostValuableProfessionalApplicationDetailsContainer";
import MostValuableProfessionalDetailsContainer from "Containers/MVP/MostValuableProfessionalDetailsContainer/MostValuableProfessionalDetailsContainer";
import MostValuableProfessionalSearchContainer from "Containers/MVP/MostValuableProfessionalSearchContainer";
import NavigationContainer from "Containers/Navigation/NavigationContainer";
import PageNotFoundContainer from "Containers/PageNotFound/PageNotFoundContainer";
import RegionalDirectorsEventContainer from "Containers/RD/Events/RegionalDirectorsEventsContainer";
import RegionalDirectorsHomeContainer from "Containers/RD/Home/RegionalDirectorsHomeContainer";
import RegionalDirectorsDetailsContainer from "Containers/RD/RegionalDirectorDetailsContainer/RegionalDirectorsDetailsContainer";
import { RegionalDirectorsContainer } from "Containers/RD/RegionalDirectorsContainer";
import SideNavigationContainer from "Containers/SideNavigation/SideNavigationContainer";
import StudentHomeContainer from "Containers/Students/Home/StudentHomeContainer";
import StudentsContainer from "Containers/Students/StudentsContainer";
import UnauthorizedContainer from "Containers/Unauthorized/UnauthorizedContainer";
import { ai } from "Services/TelemetryService";

import {
  contentStyles,
  header,
  headerContent,
  pageStyles,
  sideBarStyles,
} from "./AppContainer.styles";

initializeIcons("https://res.cdn.office.net/files/fabric-cdn-prod_20240129.001/assets/icons/");

// Configure AppInsights for the application.
ai.initialize(process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY);

const AppContainer: React.FunctionComponent = () => {
  return (
    <>
      <Stack styles={header}>
        <Stack.Item styles={headerContent}>
          <NavigationContainer /> {/* TOP NAVIGATION BAR */}
        </Stack.Item>
      </Stack>
      <Stack styles={pageStyles}>
        <Stack styles={sideBarStyles}>
          <SideNavigationContainer /> {/* SIDE NAVIGATION BAR */}
        </Stack>
        <Stack styles={contentStyles}>
          <Switch>
            {/* Common Routes and Generic Routes */}
            <Route exact path="/" component={HomeContainer} />
            <Route path="/login" component={(): ReactElement => <Login />} />
            <PrivateRoute
              exact
              path="/:tenant/access/addUser"
              component={(): ReactElement => <AccessControlCreateEditContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/access/:id/edit"
              component={(): ReactElement => <AccessControlCreateEditContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/managecodes"
              component={(): ReactElement => <ManageCodesContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/visualStudio"
              component={(): ReactElement => <VisualStudioBenefitContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/azureRequests/details/:id"
              component={(): ReactElement => <AzureBenefitRequestDetailsContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/azureRequests"
              component={(): ReactElement => <AzureRequestsContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/m365Copilot"
              component={(): ReactElement => <M365CopilotBenefitContainer />}
            />
            <PrivateRoute
              exact
              path="/:tenant/benefits/:benefitType"
              component={(): ReactElement => <BenefitsContainer />}
            />

            <PrivateRoute
              exact
              path="/:tenant/distributionLists/pending"
              component={(): ReactElement => <DistributionListRequestsContainer />}
            />

            <PrivateRoute
              exact
              path="/:tenant/distributionLists"
              component={(): ReactElement => <DistributionListsContainer />}
            />

            <PrivateRoute
              exact
              path="/:tenant/distributionLists/:emailAlias"
              component={(): ReactElement => <DistributionListDetails />}
            />

            <PrivateRoute
              exact
              path="/:tenant/awardCategories"
              component={(): ReactElement => <AwardCategoriesContainer />}
            />

            {/* Tenant Specific Student Routes */}
            <PrivateRoute exact path="/mlsa" component={StudentHomeContainer} />
            <PrivateRoute exact path="/mlsa/events" component={EventsContainer} />
            <PrivateRoute exact path="/mlsa/mlsas" component={StudentsContainer} />
            <PrivateRoute
              exact
              path="/mlsa/applications"
              component={(props: IApplicationsContainerProps): ReactElement => (
                <ApplicationsContainer {...props} tenant={TenantTypesEnum.MLSA} />
              )}
            />
            <PrivateRoute
              exact
              path="/mlsa/details/:id"
              component={(): ReactElement => <StudentDetails />}
            />
            <PrivateRoute
              exact
              path="/mlsa/access"
              component={(): ReactElement => (
                <AccessControlContainer tenant={TenantTypesEnum.MLSA} />
              )}
            />
            {/* Tenant Specific MVP Routes */}
            <PrivateRoute exact path="/mvp" component={MvpHomeContainer} />
            <PrivateRoute exact path="/mvp/events" component={MvpEventContainer} />
            <PrivateRoute
              exact
              path="/mvp/mvps"
              component={MostValuableProfessionalSearchContainer}
            />
            <PrivateRoute
              exact
              path="/mvp/details/:id"
              component={(): ReactElement => <MostValuableProfessionalDetailsContainer />}
            />
            <PrivateRoute
              exact
              path="/mvp/review/applications"
              component={(props: IApplicationsContainerProps): ReactElement => (
                <ApplicationsContainer {...props} tenant={TenantTypesEnum.MVP} />
              )}
            />
            <PrivateRoute
              exact
              path="/mvp/applications/:id"
              component={(): ReactElement => (
                <MostValuableProfessionalApplicationDetailsContainer />
              )}
            />
            <PrivateRoute
              exact
              path="/mvp/access"
              component={(): ReactElement => (
                <AccessControlContainer tenant={TenantTypesEnum.MVP} />
              )}
            />
            <PrivateRoute
              exact
              path="/mvp/review/demographics"
              component={(): ReactElement => <DemographicsContainer />}
            />
            {/* Tenant Specific RD Routes */}
            <PrivateRoute exact path="/rd" component={RegionalDirectorsHomeContainer} />
            <PrivateRoute exact path="/rd/rds" component={RegionalDirectorsContainer} />
            <PrivateRoute
              exact
              path={"/rd/details/:id"}
              component={RegionalDirectorsDetailsContainer}
            />
            <PrivateRoute exact path="/rd/events" component={RegionalDirectorsEventContainer} />
            <PrivateRoute
              exact
              path="/rd/applications"
              component={(props: IApplicationsContainerProps): ReactElement => (
                <ApplicationsContainer {...props} tenant={TenantTypesEnum.RD} />
              )}
            />
            <PrivateRoute
              exact
              path="/rd/access"
              component={(): ReactElement => <AccessControlContainer tenant={TenantTypesEnum.RD} />}
            />
            {/* Fall Thru Routes */}
            <Route path="/notFound" component={(): ReactElement => <PageNotFoundContainer />} />
            <Route path="/unauthorized" component={(): ReactElement => <UnauthorizedContainer />} />
            <Route
              path="*"
              exact={true}
              component={(): ReactElement => <PageNotFoundContainer />}
            />
          </Switch>
        </Stack>
      </Stack>
    </>
  );
};

export default AppContainer;
